import { ArrowLeft } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAppStore } from '../../app/appStore';
import { useAuthStore } from '../../features/auth/authStore';
import { openSupportChatwoot } from '../../features/support/chatwoot';
import { UnpaidDepositCard } from '../../features/wallet/UnpaidDepositCard';
import { DEPOSIT_ORDERS_QUERY_KEY, useDepositOrders } from '../../features/wallet/useDepositOrders';
import { useI18nStore } from '../../shared/i18n/i18nStore';
import { refreshWallet } from '../../shared/wallet/refreshWallet';
import { unpaidDepositOrders } from '../../shared/wallet/unpaidDeposits';
import '../../features/wallet/unpaidDeposits.css';

export function UnpaidDepositsPage() {
  const t = useI18nStore((s) => s.t);
  const language = useI18nStore((s) => s.language);
  const token = useAuthStore((s) => s.token) || '';
  const user = useAuthStore((s) => s.user);
  const activeOrderId = useAppStore((s) => s.activeOrderId);
  const goBack = useAppStore((s) => s.goBack);
  const showToast = useAppStore((s) => s.showToast);
  const queryClient = useQueryClient();
  const ordersQuery = useDepositOrders();
  const [credited, setCredited] = useState<string[]>([]);
  const nowSeconds = Math.floor(Date.now() / 1000);
  const rows = useMemo(() => unpaidDepositOrders(ordersQuery.data || [], nowSeconds).filter((o) => !credited.includes(String(o.publicId || ''))), [ordersQuery.data, nowSeconds, credited]);

  const onCredited = useCallback((publicId: string) => {
    setCredited((list) => [...list, publicId]);
    void refreshWallet(queryClient, token);
    void queryClient.invalidateQueries({ queryKey: [DEPOSIT_ORDERS_QUERY_KEY, token] });
  }, [queryClient, token]);

  const onSupport = useCallback((publicId: string) => {
    const route = publicId ? `pedidos.unpaid/${encodeURIComponent(publicId)}` : 'pedidos.unpaid';
    openSupportChatwoot({ user, language, route }).catch(() => showToast(t('supportChatError'), 'warn'));
  }, [user, language, showToast, t]);

  return (
    <section className="tab-page unpaid-page" data-unpaid-page-react="true">
      <header className="tab-page-header">
        <button className="icon-button" type="button" onClick={() => goBack('me.history')} aria-label={t('back')}>
          <ArrowLeft size={20} aria-hidden="true" />
        </button>
        <div><h1>{t('unpaidPageTitle')}</h1></div>
      </header>
      <p className="unpaid-intro">{t('unpaidPageIntroA')} <strong>{t('unpaidPageIntroEta')}</strong></p>
      {ordersQuery.isLoading ? <p className="unpaid-empty">{t('loading')}</p> : null}
      {ordersQuery.isError ? <p className="unpaid-empty" role="alert">{t('walletRefreshFailed')} <button type="button" className="unpaid-link" onClick={() => ordersQuery.refetch()}>{t('retry')}</button></p> : null}
      {!ordersQuery.isLoading && !ordersQuery.isError && rows.length === 0 ? <p className="unpaid-empty">{t('unpaidEmpty')}</p> : null}
      {rows.map((order) => (
        <UnpaidDepositCard key={String(order.publicId)} order={order} highlighted={!!activeOrderId && String(order.publicId) === activeOrderId} onCredited={onCredited} onSupport={onSupport} />
      ))}
      <button type="button" className="unpaid-older unpaid-link" onClick={() => onSupport('')}>{t('unpaidOlderNotice')}</button>
    </section>
  );
}
